import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, UserMinusIcon } from '@heroicons/react/24/outline';
import {
  useGetSessionQuery,
  useStartSessionMutation,
  useEndSessionMutation,
  useSessionCheckInMutation,
  useSessionVoidCheckInMutation,
  useGetEnrichedStudentsQuery,
  useListPickupContactsQuery,
  useListSessionObservationsQuery,
} from '../store/api/localApi';
import ObservationCreateModal from '../components/observations/ObservationCreateModal';
import IncidentCreateModal from '../components/incidents/IncidentCreateModal';
import MomentCreateModal from '../components/moments/MomentCreateModal';
import MedicationsDuePanel from '../components/medications/MedicationsDuePanel';
import AdministerModal, { AdministerSubject } from '../components/medications/AdministerModal';
import SessionSummaryStrip from '../components/sessions/SessionSummaryStrip';
import AttendanceBreakdown from '../components/sessions/AttendanceBreakdown';
import {
  AttendanceEventShape,
  AttendanceEventSource,
  AttendanceEventType,
  PickupContactShape,
  SessionShape,
  SessionStatus,
} from '../types/attendance-v2';
import { useAuth } from '../hooks/useAuth';
import { ScopedLink } from '../components/ScopedLink';
import { dublinDateString, formatTime } from '../utils/date';
import { sessionStartWarning } from './calendar/session-start-warning';
import { useToast } from '../components/Toast';
import PickupModal from '../components/staff/PickupModal';
import { useLiveSession, ChildVM } from './run-session/useLiveSession';
import ChildBox from './run-session/ChildBox';
import ChildTile from './run-session/ChildTile';
import SessionSummaryLine from './run-session/SessionSummaryLine';
import ChildHistoryPanel from './run-session/ChildHistoryPanel';

/**
 * /sessions/:sessionId — staff Run-a-session page.
 *
 * Three modes, hard separated by the session's status:
 *   - SCHEDULED   → pre-session: roster preview + big Start button
 *   - IN_PROGRESS → live: search bar, "Still expected" strip, roster grid
 *   - ENDED       → audit log
 *
 * Polls /sessions/:id every 30s while live so a second staff device sees
 * the same state within reason. Coarse but adequate at scale; a sockets
 * model would be a future win.
 */
const RunSession: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();

  const { data, isLoading, error, refetch } = useGetSessionQuery(sessionId ?? '', {
    skip: !sessionId,
    pollingInterval: 30_000,
  });
  const [startSession, { isLoading: starting }] = useStartSessionMutation();
  const [endSession, { isLoading: ending }] = useEndSessionMutation();

  if (!sessionId) {
    return <Navigate404 />;
  }
  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-slate-500">
        Loading session…
      </div>
    );
  }
  // No session on hand: only a real 404 means "not found". Anything else —
  // a Wi-Fi drop, a tablet waking from sleep, a slow cold start, a 403 — is
  // shown for what it is, with a retry. Wording "Session not found" for a
  // failed fetch sent staff off a session that was still there.
  if (!data) {
    if (errorStatus(error) === 404) return <Navigate404 />;
    return <SessionLoadError error={error} onRetry={() => void refetch()} />;
  }

  const { session, events, absentStudentIds, organizationId: sessionOrganizationId } = data;

  const handleStart = async () => {
    try {
      await startSession({ sessionId }).unwrap();
      showSuccess(`${session.className} started.`);
    } catch (err: any) {
      showError(err?.data?.message || 'Could not start session.');
    }
  };

  const handleEnd = async () => {
    try {
      const result = await endSession({ sessionId }).unwrap();
      const parts: string[] = [];
      if (result.rolledForward > 0) parts.push(`${result.rolledForward} rolled to next session`);
      if (result.closedOut > 0) parts.push(`${result.closedOut} auto-closed (flagged)`);
      showSuccess(parts.length > 0 ? `Session ended · ${parts.join(', ')}` : 'Session ended.');
    } catch (err: any) {
      showError(err?.data?.message || 'Could not end session.');
    }
  };

  return (
    <div className="py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <ScopedLink
          to="/dashboard"
          data-testid="session-back-to-today"
          className="text-sm text-slate-500 hover:underline"
        >
          ← Back to today
        </ScopedLink>

        {/* A failed poll while we still hold the session is deliberately
            invisible: RTK keeps the last good data and the next 30s poll
            heals it. Staff on a tablet should never see a ping fail. Any
            action they take meanwhile reports its own error via toast. */}
        <header className="flex items-baseline justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="session-title">
              {session.className}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {formatDate(session.date)} · {session.scheduledStart} – {session.scheduledEnd}
            </p>
          </div>
          <StatusPill status={session.status} />
        </header>

        {session.status === SessionStatus.SCHEDULED && (
          <PreSessionView session={session} onStart={handleStart} starting={starting} />
        )}

        {session.status === SessionStatus.IN_PROGRESS && (
          <LiveSessionView
            session={session}
            events={events}
            absentStudentIds={absentStudentIds}
            sessionOrganizationId={sessionOrganizationId}
            onEnd={handleEnd}
            ending={ending}
            currentUserId={user?.id ?? ''}
            currentUserName={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
            onError={showError}
            onSuccess={showSuccess}
          />
        )}

        {session.status === SessionStatus.ENDED && (
          <PostSessionView session={session} events={events} absentStudentIds={absentStudentIds} />
        )}

        {session.status === SessionStatus.CANCELLED && (
          <div className="bg-slate-100 rounded-lg p-6 text-center text-sm text-slate-600">
            This session was cancelled.{' '}
            {session.cancelledReason && <span className="italic">— {session.cancelledReason}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// =====================================================================
// Pre-session view
// =====================================================================

const PreSessionView: React.FC<{
  session: SessionShape;
  onStart: () => void;
  starting: boolean;
}> = ({ session, onStart, starting }) => {
  // Soft guard (item 10): warn — but don't block — when starting a session for
  // a different date (backfilling a missed day is legitimate) or well off the
  // scheduled time. The empty-class case is a hard block enforced by the
  // backend, surfaced via the start error toast.
  const nowHHMM = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Dublin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date());
  const warning = sessionStartWarning(
    session.date,
    session.scheduledStart,
    dublinDateString(),
    nowHHMM
  );
  const [confirmed, setConfirmed] = useState(false);

  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm font-semibold text-amber-900">
          Scheduled to start at {session.scheduledStart}
        </p>
        <p className="text-xs text-amber-800 mt-1">
          {session.expectedStudentIds.length} kids expected today.
        </p>
      </div>

      {warning && (
        <div
          data-testid="session-start-warning"
          className="bg-red-50 border border-red-300 rounded-lg p-4"
          role="alert"
        >
          <p className="text-sm font-medium text-red-900">{warning}</p>
          <label className="mt-2 flex items-center gap-2 text-sm text-red-800">
            <input
              type="checkbox"
              data-testid="session-start-warning-ack"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            Yes, start it anyway
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onStart}
          disabled={starting || (Boolean(warning) && !confirmed)}
          data-testid="session-start"
          className="px-5 py-2.5 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
        >
          {starting ? 'Starting…' : 'Start session'}
        </button>
      </div>
    </>
  );
};

// =====================================================================
// Live (in-progress) view
// =====================================================================

interface RowState {
  studentId: string;
  studentName: string;
  /** Child's DOB (ISO), when known — drives the under-6 Aistéar learning-goal
   *  gate on the observation modal opened from the roster. */
  studentDateOfBirth?: string;
  status: 'expected' | 'here' | 'gone' | 'rolled';
  lastEventAt?: string;
  /** Minutes late (positive) if expected and class has started. */
  minutesLate?: number;
}

const LiveSessionView: React.FC<{
  session: SessionShape;
  events: AttendanceEventShape[];
  absentStudentIds: string[];
  /** The session's org, resolved server-side from its location. Preferred over
   *  the caller's active-membership org for the incident modal — the latter is
   *  empty for a SUPER_ADMIN, which silently blocked the modal from opening. */
  sessionOrganizationId?: string;
  onEnd: () => void;
  ending: boolean;
  currentUserId: string;
  currentUserName: string;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}> = ({ session, events, absentStudentIds, sessionOrganizationId, onEnd, ending }) => {
  const [query, setQuery] = useState('');
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [pickupTarget, setPickupTarget] = useState<RowState | null>(null);
  // A staying-on child releasing early needs a confirm first — this holds
  // that child while the confirm is up, distinct from pickupTarget (which
  // only opens once the release is actually confirmed, or was never gated).
  const [earlyReleaseConfirmFor, setEarlyReleaseConfirmFor] = useState<ChildVM | null>(null);
  const liveSession = useLiveSession(session.id);
  const [observingFor, setObservingFor] = useState<{
    studentId: string;
    studentName: string;
    studentDateOfBirth?: string;
  } | null>(null);
  const [reportingIncidentFor, setReportingIncidentFor] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const [sharingMomentFor, setSharingMomentFor] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const [doseFor, setDoseFor] = useState<{ studentId: string; subject: AdministerSubject } | null>(
    null
  );
  const [historyFor, setHistoryFor] = useState<{ studentId: string; studentName: string } | null>(
    null
  );
  const [expandedFor, setExpandedFor] = useState<string | null>(null);
  const [checkIn] = useSessionCheckInMutation();
  const { showError } = useToast();
  const { activeOrganizationId } = useAuth();
  // The incident modal is org-scoped. Prefer the session's own org (resolved
  // server-side from its location) — the caller's active-membership org is
  // undefined for a SUPER_ADMIN, which silently blocked the modal from opening.
  const incidentOrganizationId = sessionOrganizationId ?? activeOrganizationId;

  const { data: obsData } = useListSessionObservationsQuery({ sessionId: session.id });
  const countsByStudent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of obsData?.observations ?? []) {
      if (o.status !== 'ACTIVE') continue;
      counts.set(o.studentId, (counts.get(o.studentId) ?? 0) + 1);
    }
    return counts;
  }, [obsData]);

  // Fetch the student roster so we have names for every expected kid (the
  // event stream only has names for kids who already had a tap).
  const { data: studentsData } = useGetEnrichedStudentsQuery({ locationId: session.locationId });
  const studentsById = useMemo(() => {
    const map = new Map<string, { firstName?: string; lastName?: string; dateOfBirth?: string }>();
    for (const s of studentsData?.students ?? []) {
      map.set(s.id, { firstName: s.firstName, lastName: s.lastName, dateOfBirth: s.dateOfBirth });
    }
    return map;
  }, [studentsData]);

  // Display names keyed by student id, for the medications panel — the
  // due-today board returns childId only, and a medication row without a name
  // is unusable at the trolley.
  const childNamesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const [id, info] of studentsById) {
      const name = [info.firstName, info.lastName].filter(Boolean).join(' ').trim();
      if (name) map.set(id, name);
    }
    return map;
  }, [studentsById]);

  // Per-child status/counts/groups — Task 3's view-model. Preserves the prior
  // inline derivation's semantics exactly (latest non-voided event per
  // student; announced-absent per Bug 11b).
  const { groups, counts, offSchedule } = liveSession;

  // Search filter — match by name (case-insensitive) on the query. Applied
  // per-group so each section's grid only shows its own matches.
  const q = query.trim().toLowerCase();
  const matches = (c: ChildVM) => !q || c.studentName.toLowerCase().includes(q);
  const filteredExpected = groups.expected.filter(matches);
  const filteredHere = groups.here.filter(matches);
  const filteredAnnouncedAbsent = groups.announcedAbsent.filter(matches);
  const totalMatches =
    filteredExpected.length + filteredHere.length + filteredAnnouncedAbsent.length;

  const here = groups.here.map(
    (c): RowState => ({
      studentId: c.studentId,
      studentName: c.studentName,
      studentDateOfBirth: c.studentDateOfBirth,
      status: c.status,
      lastEventAt: c.lastEventAt,
    })
  );

  const handleCheckIn = async (child: ChildVM) => {
    try {
      await checkIn({ sessionId: session.id, studentId: child.studentId }).unwrap();
    } catch (err: any) {
      showError(err?.data?.message || 'Could not check in.');
    }
  };

  // For sibling pickup, pre-fetch pickup-contact lists for every checked-in
  // kid so the modal can resolve overlaps cheaply. Skip when there's no live
  // pickup target (modal closed) to avoid the queries.
  const checkedInIds = here.map((r) => r.studentId);
  const otherCheckedInIds = pickupTarget
    ? checkedInIds.filter((id) => id !== pickupTarget.studentId)
    : [];

  // Shared by the direct release path and the early-release confirm's "yes"
  // — both ultimately open the same pickup modal for the same child.
  const toRowState = (child: ChildVM): RowState => ({
    studentId: child.studentId,
    studentName: child.studentName,
    studentDateOfBirth: child.studentDateOfBirth,
    status: child.status,
    lastEventAt: child.lastEventAt,
  });

  const renderChildTile = (child: ChildVM, extraTestId?: string) => {
    const isExpanded = expandedFor === child.studentId;
    if (isExpanded) {
      return (
        <div
          key={child.studentId}
          data-testid={extraTestId}
          style={{ gridColumn: '1 / -1' }}
          className="col-span-full"
        >
          <ChildTile
            child={child}
            onCheckIn={() => handleCheckIn(child)}
            onRelease={() => {
              if (child.stayingOn) {
                setEarlyReleaseConfirmFor(child);
                return;
              }
              setPickupTarget(toRowState(child));
            }}
            onObserve={() =>
              setObservingFor({
                studentId: child.studentId,
                studentName: child.studentName,
                studentDateOfBirth: child.studentDateOfBirth,
              })
            }
            onMoment={() =>
              setSharingMomentFor({ studentId: child.studentId, studentName: child.studentName })
            }
            onIncident={() =>
              setReportingIncidentFor({
                studentId: child.studentId,
                studentName: child.studentName,
              })
            }
            onGiveDose={() => {
              const dose = child.dueDoses[0];
              if (!dose) return;
              setDoseFor({
                studentId: child.studentId,
                subject: {
                  requirementId: dose.requirementId,
                  medicationName: dose.medicationName,
                  dosage: dose.dosage,
                  slot: dose.slot,
                  childName: child.studentName,
                },
              });
            }}
            onHistory={() =>
              setHistoryFor({ studentId: child.studentId, studentName: child.studentName })
            }
            onCollapse={() => setExpandedFor(null)}
          />
        </div>
      );
    }

    return (
      <div key={child.studentId} data-testid={extraTestId}>
        <ChildBox child={child} expanded={false} onToggle={() => setExpandedFor(child.studentId)} />
      </div>
    );
  };

  return (
    <>
      {/* One calm summary line, replacing the busy metric-tiles row. */}
      <SessionSummaryLine counts={counts} offSchedule={offSchedule} />

      {/* Activity roll-up — what has been recorded against this session so far.
          Live while running, and a recap once ended. Additive: read-only. */}
      <SessionSummaryStrip sessionId={session.id} />

      {/* Medications due for the kids actually in this session. Additive: it
          renders above the roster and changes nothing about check-in, pickup,
          observations or incidents. Renders nothing at all when no child in
          the room has a medication. */}
      <MedicationsDuePanel
        sessionId={session.id}
        locationId={session.locationId}
        childNamesById={childNamesById}
      />

      {/* Search — primary interaction at scale */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type a name… (e.g. Liam, Aoife)"
          data-testid="session-search"
          className="w-full pl-10 pr-10 py-3 text-base border-2 border-slate-300 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
        />
        <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Still-to-arrive grid — actionable at scale */}
      {filteredExpected.length > 0 && (
        <div
          data-testid="still-expected-strip"
          className="border border-amber-300 bg-amber-50 rounded-lg p-3"
        >
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
              Still to arrive · {filteredExpected.length}
            </h2>
            <span className="text-xs text-amber-700">tap a child to check in</span>
          </div>
          <div
            data-testid="session-roster"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2"
          >
            {filteredExpected.map((child) => renderChildTile(child))}
          </div>
        </div>
      )}

      {/* Here grid */}
      {filteredHere.length > 0 && (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Here · {filteredHere.length}
            </h2>
            <div data-testid="session-roster-observe-summary" className="text-xs text-slate-500">
              {countsByStudent.size} of {liveSession.children.length} kids observed today
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {filteredHere.map((child) => renderChildTile(child))}
          </div>
        </div>
      )}

      {/* Announced absent — kids whose parent filed a planned absence.
          Matches the visual weight of "Still to arrive" so it registers as a
          real roster section, but slate-toned (not amber) because it needs no
          staff action: the parent already told us. Bug 11b. */}
      {filteredAnnouncedAbsent.length > 0 && (
        <div
          data-testid="session-announced-absent"
          className="border-2 border-slate-200 bg-slate-50 rounded-lg p-3"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <UserMinusIcon className="w-4 h-4 text-slate-500" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Announced absent · {filteredAnnouncedAbsent.length}
              </span>
            </div>
            <span className="text-xs text-slate-500 italic">parent notified</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {filteredAnnouncedAbsent.map((child) =>
              renderChildTile(child, `announced-absent-${child.studentId}`)
            )}
          </div>
        </div>
      )}

      {totalMatches === 0 && query && (
        <p className="text-sm text-slate-400 italic px-2 py-3">No matches for "{query}".</p>
      )}

      <div className="pt-4 border-t border-slate-100">
        {confirmEnd ? (
          <div
            className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-3"
            data-testid="session-end-confirm"
          >
            <p className="text-sm font-semibold text-amber-900">
              {here.length} student{here.length > 1 ? 's are' : ' is'} still checked in
            </p>
            <p className="text-xs text-amber-800">
              {here.length > 1
                ? 'They will be moved into the class that starts now, if they are enrolled in it. Anyone with no next class stays listed here until you record who collected them.'
                : `${
                    here[0]?.studentName ?? 'They'
                  } will be moved into the class that starts now, if enrolled in it. Otherwise they stay listed here until you record who collected them.`}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onEnd}
                disabled={ending}
                data-testid="session-end-confirm-yes"
                className="px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                {ending ? 'Moving…' : 'Yes, move & end session'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmEnd(false)}
                data-testid="session-end-confirm-cancel"
                className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={() => (here.length > 0 ? setConfirmEnd(true) : onEnd())}
              disabled={ending}
              data-testid="session-end"
              className="px-5 py-2.5 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
            >
              {ending ? 'Ending…' : 'End session'}
            </button>
          </div>
        )}
      </div>

      {earlyReleaseConfirmFor && (
        <EarlyReleaseConfirm
          child={earlyReleaseConfirmFor}
          onCancel={() => setEarlyReleaseConfirmFor(null)}
          onConfirm={() => {
            setPickupTarget(toRowState(earlyReleaseConfirmFor));
            setEarlyReleaseConfirmFor(null);
          }}
        />
      )}

      {pickupTarget && (
        <PickupModalWithContacts
          sessionId={session.id}
          target={pickupTarget}
          otherCheckedInIds={otherCheckedInIds}
          onClose={() => setPickupTarget(null)}
        />
      )}

      {observingFor && (
        <ObservationCreateModal
          open
          onClose={() => setObservingFor(null)}
          studentId={observingFor.studentId}
          studentName={observingFor.studentName}
          studentDateOfBirth={observingFor.studentDateOfBirth}
          session={{
            id: session.id,
            className: session.className,
            scheduledStart: session.scheduledStart,
            scheduledEnd: session.scheduledEnd,
            date: session.date,
          }}
        />
      )}

      {reportingIncidentFor && incidentOrganizationId && (
        <IncidentCreateModal
          open
          onClose={() => setReportingIncidentFor(null)}
          studentId={reportingIncidentFor.studentId}
          studentName={reportingIncidentFor.studentName}
          locationId={session.locationId}
          organizationId={incidentOrganizationId}
          className={session.className}
          sessionId={session.id}
        />
      )}

      {sharingMomentFor && (
        <MomentCreateModal
          open
          onClose={() => setSharingMomentFor(null)}
          studentId={sharingMomentFor.studentId}
          studentName={sharingMomentFor.studentName}
          sessionId={session.id}
        />
      )}

      {doseFor && (
        <AdministerModal
          subject={doseFor.subject}
          locationId={session.locationId}
          onClose={() => setDoseFor(null)}
        />
      )}

      {historyFor && (
        <ChildHistoryPanel
          studentId={historyFor.studentId}
          studentName={historyFor.studentName}
          events={events}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </>
  );
};

/**
 * Guards an early release for a child who is staying on into an adjacent
 * class today (Task 8 — spec §6). The backend roll-forward itself only runs
 * at session End; releasing a "staying on" child now would remove them from
 * this session's roster with no chance for the End-time roll-forward to
 * carry them into their next class. Confirming here doesn't touch that
 * backend behaviour at all — it just opens the normal release/pickup flow.
 */
const EarlyReleaseConfirm: React.FC<{
  child: ChildVM;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ child, onCancel, onConfirm }) => {
  const id = child.studentId;
  const nextClassName = child.stayingOn?.nextClassName ?? 'their next class';
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onCancel}
        />
        <div
          data-testid={`early-release-confirm-${id}`}
          className="relative z-10 bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4"
        >
          <p className="text-sm font-semibold text-slate-900">
            {child.studentName} is enrolled in {nextClassName} next and is set to roll straight in.
          </p>
          <p className="text-sm text-slate-600">
            Releasing now means they won't continue into {nextClassName}.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onCancel}
              data-testid={`early-release-confirm-${id}-cancel`}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              Keep staying on
            </button>
            <button
              type="button"
              onClick={onConfirm}
              data-testid={`early-release-confirm-${id}-yes`}
              className="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
            >
              Release early anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Thin wrapper that fetches each sibling-candidate's pickup contacts in
 * parallel, then hands the modal a pre-keyed map so it can do its overlap
 * check synchronously. Loaded lazily — these queries only fire when staff
 * actually taps to release a kid.
 */
const PickupModalWithContacts: React.FC<{
  sessionId: string;
  target: RowState;
  otherCheckedInIds: string[];
  onClose: () => void;
}> = ({ sessionId, target, otherCheckedInIds, onClose }) => {
  // Snapshot the sibling set once on mount. The parent session polls every 30s,
  // so otherCheckedInIds can change length while the modal is open — calling a
  // hook per element (below) would then violate the Rules of Hooks and crash
  // mid-release. Freezing the set keeps the hook count stable for the modal's
  // lifetime, which is also the correct behaviour: a release-in-progress
  // shouldn't have its candidate list shift under it.
  const [frozenIds] = useState(() => otherCheckedInIds);
  // Fire all the queries via a stable map. Each dedupes through RTK Query's
  // cache so re-opens during a session don't refetch.
  const queries = frozenIds.map((sid) => useListPickupContactsQuery(sid));
  const siblingContactsByStudentId = useMemo(() => {
    const map: Record<string, PickupContactShape[]> = {};
    frozenIds.forEach((sid, idx) => {
      map[sid] = queries[idx].data?.contacts ?? [];
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frozenIds, queries.map((q) => q.data).join(',')]);
  return (
    <PickupModal
      sessionId={sessionId}
      primaryStudentId={target.studentId}
      primaryStudentName={target.studentName}
      otherCheckedInStudentIds={frozenIds}
      siblingContactsByStudentId={siblingContactsByStudentId}
      onClose={onClose}
    />
  );
};

// =====================================================================
// Post-session view (audit log + late-pickup)
// =====================================================================

const PostSessionView: React.FC<{
  session: SessionShape;
  events: AttendanceEventShape[];
  /** Live planned-absence set for the session's class+date (Bug 11b). Used to
   *  tell a genuine no-show (never arrived, no absence filed) apart from a
   *  planned absence on the ended recap. */
  absentStudentIds: string[];
}> = ({ session, events, absentStudentIds }) => {
  const [pickupTarget, setPickupTarget] = useState<RowState | null>(null);
  const { showError, showSuccess } = useToast();

  // Void a mistaken check-in from the recap. Append-only correction: the
  // original event stays in the register, marked voided (Tusla shape). A reason
  // is required — that record is the whole point of a post-hoc correction.
  const [voidCheckIn, { isLoading: voiding }] = useSessionVoidCheckInMutation();
  const [voidTarget, setVoidTarget] = useState<{ studentId: string; studentName: string } | null>(
    null
  );
  const [voidReason, setVoidReason] = useState('');

  // The one check-in event per child that Void targets: the latest non-voided
  // CHECK_IN / ROLLED_IN. Mirrors the backend's voidCheckIn, which voids the
  // student's latest live check-in AND cascade-voids the checkout sitting on top
  // of it — so an auto-end-of-session checkout does NOT make a mistaken check-in
  // un-voidable. Surfacing the action on just that one event id keeps the
  // correction unambiguous.
  const voidableCheckInEventIds = useMemo(() => {
    const latestCheckInByStudent = new Map<string, AttendanceEventShape>();
    for (const e of events) {
      if (e.voidedAt) continue;
      if (e.type !== AttendanceEventType.CHECK_IN && e.type !== AttendanceEventType.ROLLED_IN) {
        continue;
      }
      const prev = latestCheckInByStudent.get(e.studentId);
      if (!prev || e.at > prev.at) latestCheckInByStudent.set(e.studentId, e);
    }
    return new Set(Array.from(latestCheckInByStudent.values()).map((e) => e.id));
  }, [events]);

  const submitVoid = async () => {
    if (!voidTarget || voidReason.trim().length === 0) return;
    try {
      await voidCheckIn({
        sessionId: session.id,
        studentId: voidTarget.studentId,
        reason: voidReason.trim(),
      }).unwrap();
      showSuccess(`${voidTarget.studentName}'s check-in was voided.`);
      setVoidTarget(null);
      setVoidReason('');
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not void the check-in.');
    }
  };

  // Names for children who have no event today (no-shows) — the event stream
  // only carries names for kids who had a tap. Reuse the location roster.
  const { data: studentsData } = useGetEnrichedStudentsQuery({ locationId: session.locationId });
  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of studentsData?.students ?? []) {
      const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
      if (name) map.set(s.id, name);
    }
    return map;
  }, [studentsData]);

  // Split the never-arrived children into planned-absent vs no-show. A child is
  // a "no-show" only on a finished session: expected, no non-voided attendance
  // event, and no planned absence on file. Planned-absent = has an absence
  // filed (they get their own, calmer section — the parent told us).
  const { noShows, plannedAbsent } = useMemo(() => {
    const hasEvent = new Set<string>();
    for (const e of events) {
      if (!e.voidedAt) hasEvent.add(e.studentId);
    }
    const absentSet = new Set(absentStudentIds);
    const ns: Array<{ studentId: string; studentName: string }> = [];
    const pa: Array<{ studentId: string; studentName: string }> = [];
    for (const sid of session.expectedStudentIds) {
      if (hasEvent.has(sid)) continue; // arrived / rolled / checked out — not absent
      const studentName = nameOf.get(sid) ?? 'Unnamed student';
      if (absentSet.has(sid)) pa.push({ studentId: sid, studentName });
      else ns.push({ studentId: sid, studentName });
    }
    return { noShows: ns, plannedAbsent: pa };
  }, [session.expectedStudentIds, events, absentStudentIds, nameOf]);

  // Students who still need a real pickup recorded: either genuinely still
  // here (edge case) or auto-closed at session end with flagged=true.
  const needsPickup = useMemo(() => {
    const latestByStudent = new Map<string, AttendanceEventShape>();
    for (const e of events) {
      if (e.voidedAt) continue;
      const prev = latestByStudent.get(e.studentId);
      if (!prev || e.at > prev.at) latestByStudent.set(e.studentId, e);
    }
    const result: RowState[] = [];
    for (const [, evt] of latestByStudent) {
      const isHere =
        evt.type === AttendanceEventType.CHECK_IN || evt.type === AttendanceEventType.ROLLED_IN;
      const isAutoClose =
        evt.type === AttendanceEventType.CHECK_OUT &&
        evt.source === AttendanceEventSource.AUTO_END_OF_SESSION;
      if (isHere || isAutoClose) {
        result.push({
          studentId: evt.studentId,
          studentName: evt.studentName,
          status: 'here' as const,
          lastEventAt: evt.at,
        });
      }
    }
    return result;
  }, [events]);

  const sorted = [...events].sort((a, b) => a.at.localeCompare(b.at));

  return (
    <>
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
        Started by {session.startedByName ?? 'staff'} at{' '}
        {session.startedAt ? formatTime(session.startedAt) : '—'} · ended at{' '}
        {session.endedAt ? formatTime(session.endedAt) : '—'} by {session.endedByName ?? 'staff'}
      </div>

      {/* Register-style attendance breakdown (present / left early / planned
          absent / no-show), then the activity roll-up. Both read-only. */}
      <AttendanceBreakdown sessionId={session.id} />
      <SessionSummaryStrip sessionId={session.id} />

      {/* No-shows: expected, never arrived, no absence filed. The one that
          needs a second look — visually distinct from a planned absence. */}
      {noShows.length > 0 && (
        <div
          data-testid="post-session-no-shows"
          className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-amber-900">
              No-show · {noShows.length}
            </span>
            <span className="text-xs text-amber-700 italic ml-auto">
              expected, never arrived — no absence filed
            </span>
          </div>
          <ul className="space-y-1.5">
            {noShows.map((r) => (
              <li
                key={r.studentId}
                data-testid={`no-show-${r.studentId}`}
                className="flex items-center gap-3 bg-white border border-amber-200 rounded-md px-3 py-2"
              >
                <div
                  aria-hidden="true"
                  className="h-8 w-8 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center text-xs font-semibold flex-shrink-0"
                >
                  {initialsOf(r.studentName)}
                </div>
                <span className="text-sm text-slate-800">{r.studentName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Announced absent: a parent/staff filed an absence. Calm/slate — no
          action needed, distinct from the amber no-show above. */}
      {plannedAbsent.length > 0 && (
        <div
          data-testid="post-session-announced-absent"
          className="border border-slate-200 bg-slate-50 rounded-lg p-3"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Announced absent · {plannedAbsent.length}
            </span>
            <span className="text-xs text-slate-500 italic ml-auto">parent notified</span>
          </div>
          <ul className="space-y-1.5">
            {plannedAbsent.map((r) => (
              <li
                key={r.studentId}
                data-testid={`announced-absent-${r.studentId}`}
                className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-3 py-2"
              >
                <div
                  aria-hidden="true"
                  className="h-8 w-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs font-semibold flex-shrink-0"
                >
                  {initialsOf(r.studentName)}
                </div>
                <span className="text-sm text-slate-700">{r.studentName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {needsPickup.length > 0 && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-lg p-4"
          data-testid="post-session-needs-pickup"
        >
          <p className="text-sm font-semibold text-amber-900 mb-1">
            {needsPickup.length} student{needsPickup.length > 1 ? 's' : ''} still need a pickup
            record
          </p>
          <p className="text-xs text-amber-700 mb-3">
            {needsPickup.length > 1 ? 'These students were' : 'This student was'} auto-closed when
            the session ended. Tap to record who picked {needsPickup.length > 1 ? 'them' : 'them'}{' '}
            up.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {needsPickup.map((r) => (
              <RowButton key={r.studentId} row={r} onTap={(row) => setPickupTarget(row)} compact />
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
          Timeline ({sorted.length} events)
        </h2>
        <ul className="border-l-2 border-slate-100 ml-2 pl-4 space-y-3" data-testid="session-audit">
          {sorted.map((e) => {
            const voided = Boolean(e.voidedAt);
            // Correctable only if this is the child's latest live check-in (the
            // exact event the backend would void, cascade-clearing any checkout
            // on top of it).
            const canVoid = !voided && voidableCheckInEventIds.has(e.id);
            return (
              <li key={e.id} data-testid={`session-audit-event-${e.id}`}>
                <p className="text-xs text-slate-400">{formatTime(e.at)}</p>
                <p
                  className={
                    voided ? 'text-sm text-slate-400 line-through' : 'text-sm text-slate-900'
                  }
                >
                  {describeEvent(e)}
                  {voided && (
                    <span
                      className="ml-1 no-underline font-medium text-rose-500"
                      data-testid={`session-audit-event-${e.id}-voided`}
                    >
                      (voided)
                    </span>
                  )}
                  {canVoid && (
                    <button
                      type="button"
                      onClick={() =>
                        setVoidTarget({ studentId: e.studentId, studentName: e.studentName })
                      }
                      data-testid={`session-audit-event-${e.id}-void-btn`}
                      className="ml-2 text-xs font-medium text-rose-600 hover:text-rose-700 underline"
                    >
                      Void
                    </button>
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      </div>

      {pickupTarget && (
        <PickupModalWithContacts
          sessionId={session.id}
          target={pickupTarget}
          otherCheckedInIds={needsPickup
            .filter((r) => r.studentId !== pickupTarget.studentId)
            .map((r) => r.studentId)}
          onClose={() => setPickupTarget(null)}
        />
      )}

      {voidTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="void-checkin-modal"
        >
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl p-5">
            <h3 className="text-base font-semibold text-slate-900">
              Void {voidTarget.studentName}'s check-in?
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              This corrects the register — the original check-in stays on record, marked voided. Say
              why so the correction is auditable.
            </p>
            <label className="mt-3 block text-xs font-medium text-slate-500" htmlFor="void-reason">
              Reason
            </label>
            <textarea
              id="void-reason"
              data-testid="void-checkin-reason"
              value={voidReason}
              onChange={(ev) => setVoidReason(ev.target.value)}
              maxLength={500}
              rows={2}
              placeholder="e.g. tapped in by mistake — the child wasn't here"
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-rose-500 focus:ring-2 focus:ring-rose-200 focus:outline-none"
            />
            <div className="mt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setVoidTarget(null);
                  setVoidReason('');
                }}
                data-testid="void-checkin-cancel"
                className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitVoid}
                disabled={voiding || voidReason.trim().length === 0}
                data-testid="void-checkin-confirm"
                className="px-4 py-2 rounded-md bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
              >
                {voiding ? 'Voiding…' : 'Void check-in'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// =====================================================================
// Bits and pieces
// =====================================================================

const RowButton: React.FC<{
  row: RowState;
  onTap: (r: RowState) => void;
  disabled?: boolean;
  compact?: boolean;
}> = ({ row, onTap, disabled, compact }) => {
  const bg =
    row.status === 'here'
      ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
      : row.status === 'gone'
      ? 'bg-slate-100 border-slate-200 opacity-70'
      : row.status === 'rolled'
      ? 'bg-blue-50 border-blue-200'
      : (row.minutesLate ?? 0) > 0
      ? 'bg-rose-50 border-rose-200 hover:bg-rose-100'
      : 'bg-amber-50 border-amber-200 hover:bg-amber-100';
  const initials =
    row.studentName
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  return (
    <button
      type="button"
      onClick={() => onTap(row)}
      disabled={disabled || row.status === 'gone' || row.status === 'rolled'}
      data-testid={`session-row-${row.studentId}`}
      data-status={row.status}
      className={`${bg} border rounded-md px-2 py-1.5 text-left flex items-center gap-2 transition disabled:cursor-default`}
    >
      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-900 truncate">{row.studentName}</p>
        {!compact && (
          <p
            className={`text-[10px] ${
              row.status === 'here'
                ? 'text-emerald-700'
                : row.status === 'rolled'
                ? 'text-blue-700'
                : row.status === 'gone'
                ? 'text-slate-500'
                : (row.minutesLate ?? 0) > 0
                ? 'text-rose-700'
                : 'text-amber-700'
            }`}
          >
            {row.status === 'here' && row.lastEventAt && `In ${formatTime(row.lastEventAt)}`}
            {row.status === 'gone' && row.lastEventAt && `Out ${formatTime(row.lastEventAt)}`}
            {row.status === 'rolled' && 'Rolled to next'}
            {row.status === 'expected' &&
              ((row.minutesLate ?? 0) > 0 ? `⚠ ${row.minutesLate}m late` : 'Expected')}
          </p>
        )}
        {compact && row.status === 'expected' && (row.minutesLate ?? 0) > 0 && (
          <p className="text-[10px] text-rose-700">⚠ {row.minutesLate}m late</p>
        )}
      </div>
    </button>
  );
};

const StatusPill: React.FC<{ status: SessionStatus }> = ({ status }) => {
  const cls =
    status === SessionStatus.IN_PROGRESS
      ? 'text-emerald-700 bg-emerald-100'
      : status === SessionStatus.ENDED
      ? 'text-slate-500 bg-slate-100'
      : status === SessionStatus.CANCELLED
      ? 'text-rose-700 bg-rose-100'
      : 'text-slate-500 bg-slate-100';
  const label =
    status === SessionStatus.IN_PROGRESS
      ? 'Live'
      : status.charAt(0) + status.slice(1).toLowerCase().replace('_', ' ');
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${cls}`}
      data-testid="session-status"
    >
      {status === SessionStatus.IN_PROGRESS && (
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-pulse" />
      )}
      {label}
    </span>
  );
};

function errorStatus(error: unknown): number | string | undefined {
  return (error as { status?: number | string } | undefined)?.status;
}

/**
 * Nothing to show and the request failed for a reason other than 404. The
 * backend's own message wins when it sent one (403 NOT_ASSIGNED_TO_CLASS,
 * INVALID_CONTEXT); a network failure gets a plain line and a retry.
 */
const SessionLoadError: React.FC<{ error: unknown; onRetry: () => void }> = ({
  error,
  onRetry,
}) => {
  const status = errorStatus(error);
  const backendMessage = (error as { data?: { message?: string } } | undefined)?.data?.message;
  const message =
    backendMessage ??
    (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR'
      ? "Can't reach the server. Check the connection and try again."
      : 'Could not load this session.');
  return (
    <div
      data-testid="session-load-error"
      className="min-h-[40vh] flex flex-col items-center justify-center gap-3 text-sm text-slate-600"
    >
      <p>{message}</p>
      <button
        type="button"
        data-testid="session-retry-btn"
        onClick={onRetry}
        className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
      >
        Try again
      </button>
      <ScopedLink to="/dashboard" className="text-primary-600 hover:underline">
        Back to dashboard
      </ScopedLink>
    </div>
  );
};

const Navigate404: React.FC = () => (
  <div
    data-testid="session-not-found"
    className="min-h-[40vh] flex flex-col items-center justify-center gap-3 text-sm text-slate-500"
  >
    <p>Session not found.</p>
    <ScopedLink to="/dashboard" className="text-primary-600 hover:underline">
      Back to dashboard
    </ScopedLink>
  </div>
);

function initialsOf(name: string): string {
  return (
    name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

function formatDate(yyyyMmDd: string): string {
  try {
    const d = new Date(yyyyMmDd + 'T00:00:00Z');
    return d.toLocaleDateString('en-IE', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC', // the string is a civil date anchored at T00:00:00Z
    });
  } catch {
    return yyyyMmDd;
  }
}

export function describeEvent(e: AttendanceEventShape): string {
  const who = e.studentName;
  switch (e.type) {
    case AttendanceEventType.CHECK_IN:
      return `${who} checked in · by ${e.byName}`;
    case AttendanceEventType.ROLLED_IN:
      return `${who} rolled in${e.notes ? ` · ${e.notes}` : ''}`;
    case AttendanceEventType.ROLLED_OUT:
      return `${who} rolled out${e.notes ? ` · ${e.notes}` : ''}`;
    case AttendanceEventType.CHECK_OUT: {
      const flag = e.releasedTo?.flagged ? '🚩 ' : '';
      const to = e.releasedTo?.name ?? 'unknown';
      const note = e.releasedTo?.verificationNote
        ? ` — note: "${e.releasedTo.verificationNote}"`
        : '';
      return `${flag}${who} released to ${to}${note} · by ${e.byName}`;
    }
  }
}

export default RunSession;
